import React, {
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { handleApiErrors } from 'helpers';
import { useNewApiCall } from 'hooks';
import moment from 'moment';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Divider } from 'primereact/divider';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputMask, InputMaskChangeEvent } from 'primereact/inputmask';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  EmployeesService,
  SchedulePersonShiftService
} from 'components/services';
import ShiftSchema from 'constants/schemas/ShiftSchema';
import ConfirmationDialog from 'ui/shared/ConfirmationDialog';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';

import { Shift } from '../../types/interfaces';

const nestedDialogStyle: React.CSSProperties = {
  width: 'min(500px, calc(100vw - 1rem))',
  maxWidth: 'calc(100vw - 1rem)',
  maxHeight: 'calc(100vh - 1rem)'
};

type FormValues = z.infer<typeof ShiftSchema>;

interface RefInterface {
  handleCancel: () => void;
}

interface ShiftDialogProps {
  shift: Shift | null;
  selectedDateFilter: Date | null;
  onSave: (shift: Shift) => void;
  onDelete: (guid: string) => void;
  onClose: () => void;
  departmentId?: string;
  employeeId?: string;
  ref?: RefObject<RefInterface | null>;
  setIsCalendarOpen: (isOpen: boolean) => void;
}

const ShiftDialog = React.forwardRef<RefInterface, ShiftDialogProps>(
  (
    {
      shift,
      selectedDateFilter,
      onSave,
      onDelete,
      onClose,
      departmentId,
      employeeId,
      setIsCalendarOpen
    }: ShiftDialogProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      handleSubmit,
      setValue,
      setError,
      reset,
      watch,
      formState: { isDirty, errors }
    } = useForm<FormValues>({
      resolver: zodResolver(ShiftSchema),
      defaultValues: {
        personId: employeeId ? parseInt(employeeId) : 0,
        date: moment().format('YYYY-MM-DD'),
        workdayStart: '08:00',
        workdayDuration: '08:00',
        breakDuration: '01:00',
        guid: '',
        departmentId: Number(departmentId),
        isTemplate: false
      }
    });
    const minDate = selectedDateFilter
      ? new Date(
          selectedDateFilter.getFullYear(),
          selectedDateFilter.getMonth(),
          1
        )
      : null;

    const maxDate = selectedDateFilter
      ? new Date(
          selectedDateFilter.getFullYear(),
          selectedDateFilter.getMonth() + 1,
          0
        )
      : null;

    const guid = watch('guid');
    const personId = watch('personId');
    const unsavedShift = guid && !guid.startsWith('temp-');

    const toast = useRef<Toast>(null);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const [executorsTable, setExecutorsTable] = useState<any[]>([]);
    const handleBreakChange = (e: InputMaskChangeEvent) => {
      setValue('breakDuration', e.value || '', {
        shouldDirty: true,
        shouldValidate: true
      });
    };

    const getExecutorsTableApi = useCallback(async () => {
      if (!departmentId) {
        setExecutorsTable([]);
        return;
      }
      try {
        const employees = (
          await EmployeesService.getByDepartments(parseInt(departmentId))
        ).data;
        if (employees?.value) {
          setExecutorsTable(
            employees.value.map((x: any) => ({
              ...x,
              key: x.id.toString(),
              id: x.id,
              label: x.fullName?.toString() || ''
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить сотрудников'
        });
      }
    }, [departmentId]);

    useNewApiCall(getExecutorsTableApi, [departmentId]);

    useEffect(() => {
      if (shift) {
        reset({
          ...shift
        });
      }
    }, [shift, reset]);

    const handleCancel = () => {
      if (isDirty) {
        setShowUnsavedDialog(true);
      } else {
        onClose();
      }
    };

    useImperativeHandle(ref, () => ({ handleCancel }));

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
      const formatted: Shift = {
        ...data,
        breakDuration:
          data.breakDuration.length > 0 ? data.breakDuration : '00:00',
        date: moment(data.date).format('YYYY-MM-DD')
      } as Shift;

      const validationResult =
        await SchedulePersonShiftService.validatePersonShift(
          formatted.personId,
          formatted
        );
      if (validationResult?.status === 200) {
        onSave(formatted);
      } else {
        handleApiErrors({
          error: validationResult?.data?.error,
          setError,
          fieldMapping: {
            ShiftInterfere: {
              field: 'date',
              message:
                'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название вида отсутствия'
            },
            SameShortName: {
              field: 'shortName',
              message:
                'Запись с таким коротким названием уже есть в справочнике. Пожалуйста, укажите уникальное короткое название вида отсутствия'
            }
          },
          customHandlers: {
            ShiftInterfere: () =>
              handleShiftConflict(validationResult?.data?.errorObject),
            ShiftConflict: () =>
              handleShiftConflict(validationResult?.data?.errorObject), // Обработка новой ошибки
            AbsenceConflict: () =>
              handleAbsenceConflict(validationResult?.data?.errorObject), // Обработка новой ошибки
            MaxMonthlyScheduledHours: () =>
              handleRuleValidationError(
                'MaxMonthlyScheduledHours',
                validationResult?.data?.errorObject
              ),
            MaxConsecutiveDaysWithShifts: () =>
              handleRuleValidationError(
                'MaxConsecutiveDaysWithShifts',
                validationResult?.data?.errorObject
              )
          }
        });
      }
    };

    const handleShiftConflict = (conflicts: any[] | undefined) => {
      // Преобразуем в массив если пришел одиночный объект
      const conflictList = Array.isArray(conflicts)
        ? conflicts
        : conflicts
        ? [conflicts]
        : [];

      if (conflictList.length === 0) {
        setError('date', {
          type: 'manual',
          message: 'Обнаружен конфликт смен'
        });
        return;
      }

      // Формируем детальное сообщение для тоста
      let conflictDetails = 'Обнаружены конфликтующие смены:\n';
      conflictList.forEach((conflict, index) => {
        const start = moment(conflict.workdayStart, 'HH:mm');
        const durationParts = conflict.workdayDuration.split(':').map(Number);
        const end = start
          .clone()
          .add(durationParts[0], 'hours')
          .add(durationParts[1], 'minutes');

        conflictDetails += `\n${index + 1}. ${
          conflict.departmentName || 'Подразделение не указано'
        } - ${moment(conflict.date).format('DD.MM.YYYY')} ${start.format(
          'HH:mm'
        )}-${end.format('HH:mm')}`;
      });

      // Устанавливаем общую ошибку для поля даты
      setError('date', {
        type: 'manual',
        message: `Найдено ${conflictList.length} конфликтующих смен`
      });

      // Показываем детали в тосте
      toast.current?.show({
        severity: 'warn',
        summary: 'Конфликт смен',
        detail: conflictDetails,
        life: 15000 // Увеличиваем время показа
      });
    };

    const handleAbsenceConflict = (conflicts: any[] | undefined) => {
      const conflictList = Array.isArray(conflicts)
        ? conflicts
        : conflicts
        ? [conflicts]
        : [];

      if (conflictList.length === 0) {
        setError('date', {
          type: 'manual',
          message: 'Обнаружен конфликт с отсутствием'
        });
        return;
      }

      let conflictDetails = 'Обнаружены конфликтующие отсутствия:\n';
      conflictList.forEach((conflict, index) => {
        const from = moment(conflict.from).format('DD.MM.YYYY HH:mm');
        const to = moment(conflict.to).format('DD.MM.YYYY HH:mm');

        conflictDetails += `\n${index + 1}. ${
          conflict.absence.label
        } (${from} - ${to})`;
      });

      setError('date', {
        type: 'manual',
        message: `Найдено ${conflictList.length} конфликтующих отсутствий. Сначала удалите или измените их.`
      });

      toast.current?.show({
        severity: 'warn',
        summary: 'Конфликт с отсутствиями',
        detail: conflictDetails,
        life: 15000
      });
    };

    const handleRuleValidationError = (errorCode: string, errorObject: any) => {
      const RULE_CONFIG: Record<string, { name: string; unit: string }> = {
        MaxMonthlyScheduledHours: {
          name: 'Максимальное количество запланированных часов за месяц',
          unit: 'часов'
        },
        MaxConsecutiveDaysWithShifts: {
          name: 'Максимальное количество смен подряд',
          unit: 'смен подряд'
        }
      };

      const ruleConfig = RULE_CONFIG[errorCode] || {
        name: 'Неизвестное правило',
        unit: ''
      };

      const settingValue = errorObject?.settingValue;

      let errorMessage = `Нарушается правило: ${ruleConfig.name}`;

      if (typeof settingValue === 'number' && ruleConfig.unit) {
        errorMessage += ` (лимит: ${settingValue} ${ruleConfig.unit})`;
      }

      // Ошибки валидации правил отображаем на поле длительности смены
      setError('workdayDuration', {
        type: 'manual',
        message: errorMessage
      });

      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: errorMessage,
        life: 5000
      });
    };

    const handleDelete = () => setShowDeleteDialog(true);

    const confirmDelete = () => {
      if (unsavedShift) {
        onDelete(guid);
      }
      setShowDeleteDialog(false);
      onClose();
    };

    const handleEmployeeChange = (event: DropdownChangeEvent) => {
      setValue('personId', event.value ?? 0, {
        shouldDirty: true,
        shouldValidate: true
      });
    };

    const entityNameDelete = `сотруднику ${executorsTable.find(
      (item) => item.id === personId
    )?.label} от ${watch('date')}`;

    return (
      <div className="dialog-tabless-card calendarAssingment-card__roles">
        <Toast ref={toast} />
        <Divider />

        <div className="calendarAssingment-card__column">
          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="employee" className="shift-label p-0">
                  Сотрудник<span className="required">*</span>:
                </label>
                <div className="col p-0">
                  <Dropdown
                    id="employee"
                    value={personId !== 0 ? personId : undefined}
                    options={executorsTable}
                    onChange={handleEmployeeChange}
                    optionLabel="label"
                    optionValue="id"
                    placeholder="Выберите сотрудника"
                    className="w-full"
                    filter
                    showClear
                  />
                </div>
              </div>
              <p
                className={`task-type-card-errors card-errors ${
                  errors.personId ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.personId ? (errors.personId.message as string) : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="date" className="shift-label p-0">
                  Дата<span className="required">*</span>:
                </label>
                <div className="col p-0">
                  <Calendar
                    id="date"
                    nextIcon="none"
                    prevIcon="none"
                    value={watch('date') ? new Date(watch('date')) : null}
                    onChange={(e) => {
                      let val: any = e.value;
                      if (Array.isArray(val)) val = val[0] ?? null;
                      setValue(
                        'date',
                        val ? moment(val).format('YYYY-MM-DD') : '',
                        {
                          shouldDirty: true,
                          shouldValidate: true
                        }
                      );
                    }}
                    dateFormat="dd.mm.yy"
                    showIcon
                    className="w-full"
                    minDate={minDate ?? undefined}
                    maxDate={maxDate ?? undefined}
                    onShow={() => setIsCalendarOpen(true)}
                    onHide={() => setIsCalendarOpen(false)}
                  />
                </div>
              </div>
              <p
                className={`task-type-card-errors card-errors ${
                  errors.date ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.date ? (errors.date.message as string) : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="workdayStart" className="shift-label p-0">
                  Начало смены<span className="required">*</span>:
                </label>
                <div className="col p-0">
                  <InputMask
                    mask="99:99"
                    value={watch('workdayStart')}
                    onChange={(e) =>
                      setValue('workdayStart', e.value || '', {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                    placeholder="ЧЧ:ММ"
                    slotChar="ЧЧ:ММ"
                    className="compact-input"
                  />
                </div>
              </div>
              <p
                className={`task-type-card-errors card-errors ${
                  errors.workdayStart ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.workdayStart
                  ? (errors.workdayStart.message as string)
                  : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="workdayDuration" className="shift-label p-0">
                  Длительность<span className="required">*</span>:
                </label>
                <div className="col p-0">
                  <InputMask
                    mask="99:99"
                    value={watch('workdayDuration')}
                    onChange={(e) =>
                      setValue('workdayDuration', e.value || '', {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                    placeholder="ЧЧ:ММ"
                    slotChar="ЧЧ:ММ"
                    className="compact-input"
                  />
                </div>
              </div>
              <p
                className={`task-type-card-errors card-errors ${
                  errors.workdayDuration ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.workdayDuration
                  ? (errors.workdayDuration.message as string)
                  : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="breakDuration" className="shift-label p-0">
                  Продолжительность перерыва:
                </label>
                <div className="col p-0">
                  <InputMask
                    mask="99:99"
                    value={watch('breakDuration')}
                    onChange={handleBreakChange}
                    placeholder="00:00"
                    slotChar="ЧЧ:ММ"
                    className="compact-input"
                  />
                </div>
              </div>
              <p
                className={`task-type-card-errors card-errors ${
                  errors.breakDuration ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.breakDuration
                  ? (errors.breakDuration.message as string)
                  : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-start gap-3 w-full">
                <label htmlFor="comment" className="shift-label">
                  Комментарий сотрудника:
                </label>
                <div className="col p-0">
                  <InputTextarea
                    id="comment"
                    value={watch('comment')}
                    disabled
                    className="w-full"
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </div>
              </div>
              <p className="task-type-card-errors card-errors text-transparent">
                {' '}
              </p>
            </div>
          </div>

          <div className="default-card__buttons">
            <div className="flex gap-3">
              <Button
                label={unsavedShift ? 'Применить' : 'Добавить смену'}
                icon="pi pi-check"
                type="submit"
                className={unsavedShift ? '' : 'p-button-success'}
                onClick={handleSubmit(onSubmit)}
              />
              {unsavedShift && (
                <Button
                  label="Удалить"
                  icon="pi pi-trash"
                  onClick={handleDelete}
                  className="p-button-danger"
                />
              )}
              <Button
                label="Отмена"
                icon="pi pi-times"
                onClick={handleCancel}
                className="p-button-secondary"
              />
            </div>
          </div>
        </div>

        <ConfirmationDialog
          visible={showUnsavedDialog}
          dialogStyle={nestedDialogStyle}
          onCancel={() => setShowUnsavedDialog(false)}
          onContinue={onClose}
          message="Есть несохранённые изменения. Вы хотите продолжить без сохранения?"
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={() => setShowDeleteDialog(false)}
          onConfirm={confirmDelete}
          width="min(500px, calc(100vw - 1rem))"
          entityType="смену"
          entityName={entityNameDelete || ''}
        />
      </div>
    );
  }
);

ShiftDialog.displayName = 'ShiftDialog';

export default ShiftDialog;
